import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'SavedViewsDrawer' }

import { ref, reactive, computed, watch, nextTick, onBeforeUnmount, getCurrentInstance, set } from 'vue'
import swal from 'sweetalert2'
import { useSavedViewsDrawer, closeSavedViews } from '@/composables/useSavedViewsDrawer'
import {
  fetchSavedFilters,
  renamePreset,
  deletePreset,
  setPresetDefault,
  type SavedFilterRow,
} from '@/util/savedFiltersApi'
import {
  SAVED_VIEW_REGISTRY,
  getSavedViewEntry,
  getSavedViewEntity,
  buildSavedViewRoute,
  matchSavedViewKey,
  type SavedViewRegistryEntry,
} from '@/util/savedViewsRegistry'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const state = useSavedViewsDrawer()
const inst = getCurrentInstance()
const vm = inst ? (inst.proxy as any) : null

const rows = ref<SavedFilterRow[]>([])
const loading = ref(false)
const loadError = ref(false)
const expanded = reactive<Record<string, boolean>>({})

const editingId = ref<number | null>(null)
const editName = ref('')
const editInput = ref<HTMLInputElement | HTMLInputElement[] | null>(null)

// The view_key of the page the user is currently on (route path + query, so a
// per-entity board like /projects/12 and a tab like ?tab=2 both resolve).
const currentViewKey = computed<string | null>(() => {
  const path = vm?.$route?.path
  if (!path) return null
  return matchSavedViewKey(path, vm?.$route?.query || {})
})

// Named presets only (the name === '' last-used row is invisible plumbing).
const presetsByKey = computed<Record<string, SavedFilterRow[]>>(() => {
  const map: Record<string, SavedFilterRow[]> = {}
  for (const r of rows.value) {
    if (r.name === '') continue
    if (!map[r.view_key]) map[r.view_key] = []
    map[r.view_key].push(r)
  }
  return map
})

// Sections in display order: current page first, then registry order. Only
// registered view_keys with presets are shown; every page that saves views must
// have a registry entry, or its views are invisible here.
const sections = computed(() => {
  const byKey = presetsByKey.value
  const cur = currentViewKey.value
  const ordered: string[] = []
  if (cur && byKey[cur] && getSavedViewEntry(cur)) ordered.push(cur)
  for (const e of SAVED_VIEW_REGISTRY) {
    if (e.key !== cur && byKey[e.key]) ordered.push(e.key)
  }
  return ordered.map((k) => ({
    key: k,
    entry: getSavedViewEntry(k)!,
    presets: byKey[k],
  }))
})

async function load(): Promise<void> {
  loading.value = true
  loadError.value = false
  try {
    rows.value = await fetchSavedFilters()
    // Current page's section expanded, the rest collapsed.
    const cur = currentViewKey.value
    for (const k of Object.keys(presetsByKey.value)) set(expanded, k, k === cur)
  } catch (e) {
    loadError.value = true
  } finally {
    loading.value = false
  }
}

function onExpand(key: string, val: boolean) {
  set(expanded, key, val)
}

// Compact, generic preview of a stored filter blob (values are page-owned, so
// count active fields rather than trying to render each shape).
function isActiveVal(v: any): boolean {
  if (v == null || v === '') return false
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'object') return Object.values(v).some((x) => x !== '' && x != null && x !== false)
  return true
}
function filterSummary(filters: Record<string, any>): string {
  let c = 0
  for (const k in filters) {
    if (k.startsWith('_')) continue
    if (isActiveVal(filters[k])) c++
  }
  if (c === 0) return 'No filters'
  return c === 1 ? '1 filter' : `${c} filters`
}
// Per-entity views lead with the entity name (which project or survey) so two
// views with the same name on different boards can be told apart.
function rowMeta(filters: Record<string, any>): string {
  const ent = getSavedViewEntity(filters)
  const summary = filterSummary(filters)
  return ent && ent.name ? `${ent.name} · ${summary}` : summary
}

function close() {
  closeSavedViews()
}

function openView(entry: SavedViewRegistryEntry, preset: SavedFilterRow) {
  if (!vm) return
  const target = buildSavedViewRoute(entry, preset.filters)
  if (!target) return
  closeSavedViews()
  const cur = vm.$route
  const samePage =
    cur.path === target.path &&
    Object.keys(target.query).every((k) => String(cur.query[k] == null ? '' : cur.query[k]) === target.query[k])
  if (samePage) {
    // Same page: change the query so FilterBar's view-param watcher applies it.
    vm.$router.replace({ path: target.path, query: { ...cur.query, view: preset.id } }).catch(() => {})
  } else {
    vm.$router.push({ path: target.path, query: { ...target.query, view: preset.id } }).catch(() => {})
  }
}

function startRename(p: SavedFilterRow) {
  editingId.value = p.id
  editName.value = p.name
  nextTick(() => {
    const el = Array.isArray(editInput.value) ? editInput.value[0] : editInput.value
    el?.focus()
  })
}
function cancelRename() {
  editingId.value = null
  editName.value = ''
}
async function commitRename(p: SavedFilterRow) {
  const name = editName.value.trim()
  if (!name) return cancelRename()
  if (name === p.name) return cancelRename()
  try {
    await renamePreset(p.id, name)
    await load()
  } catch (e: any) {
    swal('Error', e?.response?.status === 409 ? 'A view with that name already exists' : 'Could not rename the view', 'error')
  } finally {
    cancelRename()
  }
}

async function toggleDefault(p: SavedFilterRow) {
  const next: 0 | 1 = p.is_default ? 0 : 1
  // Flip in place instead of reloading. A full load() re-fetches every row and
  // resets which sections are expanded, so the section you just starred would
  // collapse and the list would flicker through its loading state. Update the
  // reactive rows directly (the star binds to is_default) and only one default
  // per view_key, so clear siblings when setting one on.
  const prev = rows.value.map((r) => ({ id: r.id, is_default: r.is_default }))
  for (const r of rows.value) {
    if (r.id === p.id) r.is_default = next
    else if (next === 1 && r.view_key === p.view_key && r.name !== '') r.is_default = 0
  }
  try {
    await setPresetDefault(p.id, next === 1)
  } catch (e) {
    const byId = new Map(prev.map((x) => [x.id, x.is_default]))
    for (const r of rows.value) { const v = byId.get(r.id); if (v != null) r.is_default = v }
    swal('Error', 'Could not update the default view', 'error')
  }
}

async function onDelete(p: SavedFilterRow) {
  const res = await swal({
    title: 'Delete view?',
    text: `"${p.name}" will be removed.`,
    type: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#fd5d93',
    cancelButtonText: 'Cancel',
  })
  if (!res.value) return
  try {
    await deletePreset(p.id)
    await load()
  } catch (e) {
    swal('Error', 'Could not delete the view', 'error')
  }
}

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && state.isOpen && editingId.value == null) close()
}

watch(
  () => state.isOpen,
  (open) => {
    if (open) {
      document.addEventListener('keydown', onKey)
      load()
    } else {
      document.removeEventListener('keydown', onKey)
      cancelRename()
    }
  },
)

onBeforeUnmount(() => document.removeEventListener('keydown', onKey))

return { __sfc: true,state, inst, vm, rows, loading, loadError, expanded, editingId, editName, editInput, currentViewKey, presetsByKey, sections, load, onExpand, isActiveVal, filterSummary, rowMeta, close, openView, startRename, cancelRename, commitRename, toggleDefault, onDelete, onKey }
}

})